import { Suspense, useState } from 'react'

import { ChevronDown, LayoutDashboard, Library } from 'lucide-react'

import { lazyNamed } from '@/utils/lazy-named'
import { cn } from '@/utils/ui/styles'

import { HasRole } from '@/shared/auth/components/has-role'
import { ROLES } from '@/shared/auth/constants'

import { ErrorBoundary } from '@/components/common/error-boundary'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubItem,
	useSidebar,
} from '@/components/ui/sidebar'

import { SidebarLink, SidebarSubLink } from './sidebar-link'

// Lazy on purpose (P-21/P-23): the entries module is what pulls the dashboards
// query surface into the shell, so it stays out of the main chunk until the
// group actually renders. All three exports
// come from the ONE module, so the collapsed popover, the expanded list and
// the Library row's count share a chunk, a hook/query and a row component
// rather than composing the list (or re-fetching the config) twice.
const SidebarDashboardEntries = lazyNamed(
	() => import('./sidebar-dashboard-entries'),
	'SidebarDashboardEntries',
)
const SidebarDashboardsCollapsedMenu = lazyNamed(
	() => import('./sidebar-dashboard-entries'),
	'SidebarDashboardsCollapsedMenu',
)
const SidebarLibraryLink = lazyNamed(
	() => import('./sidebar-dashboard-entries'),
	'SidebarLibraryLink',
)

/**
 * Own small group. Read Only+, NOT Analyst+ like Playbooks/Actions:
 * viewing a dashboard needs only `ReadCases` + `ReadDashboards`, both of which
 * Read Only holds. The gate moves with the route's — a stricter item here would
 * leave Read Only a surface with no way in. Write affordances inside the
 * feature are gated one by one.
 */
export function SidebarCustomDashboards() {
	return (
		<HasRole role={ROLES.ReadOnly}>
			<SidebarCustomDashboardsGroup />
		</HasRole>
	)
}

/**
 * The parent entry: the route itself, with no `?dashboard=`, resolving to
 * whatever the landing chain picks.
 *
 * `exact` because with nested entries the parent must light only on the index —
 * a prefix match would double-light it alongside the Library entry.
 *
 * This is the SHAPE the nav is guaranteed to have. It renders on its own while
 * the entries chunk loads and if that chunk fails, and the collapsed popover
 * below only ever *upgrades* it, so no state of the dashboards read can cost
 * the user the way in (P-17).
 */
function DashboardsParentLink() {
	return (
		<SidebarLink tooltip="Custom Dashboards" to="/custom-dashboards" exact>
			<LayoutDashboard />
			<span>Custom Dashboards</span>
		</SidebarLink>
	)
}

/**
 * The Library row without its count — the fallback for `SidebarLibraryLink`
 * below (pending/failed config read, or the lazy chunk itself failing) and
 * the ONE spelling of "plain, count-less Library row" so the real row and its
 * fallback cannot drift in everything except the number. Same P-17 reasoning
 * as `DashboardsParentLink`: the Library entry is static nav, so it must
 * survive the config read costing it nothing but the number.
 */
function LibraryLink() {
	return (
		<SidebarMenuSubItem>
			<SidebarSubLink
				// Deliberately tab-less: the Library's resting state is the
				// Dashboards tab.
				to="/custom-dashboards/library"
			>
				<Library />
				<span>Library</span>
			</SidebarSubLink>
		</SidebarMenuSubItem>
	)
}

/**
 * The group body, mounted only for ReadOnly+ — the wrapper above is that role
 * check and nothing else — so the lazy entries chunk and its config query never
 * load or fire for anyone below it. Demo and prod render this group like every
 * other environment: the build-time exclusions this comment once named were
 * removed when the feature shipped (ADR 0027).
 *
 * Shape: the parent entry, then the Tenant default and up to four of this
 * user's pins (`use-sidebar-dashboards.ts` owns the rule — ruling Q33), then
 * Library. The parent + Library always render — the parent through
 * `DashboardsParentLink`, Library through `LibraryLink` — both of which double
 * as fallbacks below — so a pending or failed config read costs the shortcuts
 * (and the Library count) and nothing else, and the nav always navigates
 * (P-17).
 *
 * Icon-collapsed, `SidebarMenuSub` hides itself, so the group is exactly one
 * icon: no stacked per-dashboard icons and no count badge.
 * `SidebarDashboardsCollapsedMenu` is what keeps the shortcuts reachable there,
 * by wrapping that one icon in a hover/focus popover carrying the same list;
 * with nothing to list it renders the plain tooltip'd link untouched.
 */
function SidebarCustomDashboardsGroup() {
	// Expanded (and on mobile, where the rail is a sheet), the parent entry is
	// exactly what it was before the shortcuts — the popover branch is not
	// mounted at all, so the expanded sidebar cannot be changed by it. The
	// condition is `SidebarMenuButton`'s own tooltip gate, spelled
	// `hidden={state !== 'collapsed' || isMobile}` in `components/ui/sidebar.tsx`.
	const { state, isMobile } = useSidebar()
	const collapsedToIcon = state === 'collapsed' && !isMobile

	/**
	 * Whether THIS group's own sub-list is open — a different axis from
	 * `collapsedToIcon` above (the whole rail shrinking to icons). Local
	 * `useState`, not a P-20 store, and that is a considered choice, not an
	 * oversight:
	 *
	 * `SidebarCustomDashboardsGroup` lives inside the app shell, which mounts
	 * once for the life of the authenticated session and is never part of the
	 * routed tree — so this state ALREADY survives every navigation with no
	 * help from anywhere else, the same way `useState` in any other
	 * always-mounted shell chrome would. The one thing a store would add is
	 * surviving a full page reload, and for a disclosure toggle over static
	 * nav chrome (not a draft, not a form, nothing the user would consider
	 * "lost") defaulting back to `true` (expanded) on reload is the right
	 * behaviour, not a gap to paper over — it's also then indistinguishable
	 * from what every user saw before this toggle existed. So there is no
	 * server data to duplicate (P-06 is moot) and no cross-component reader
	 * that would need a store's atomic-hook seam (P-20) — a plain `useState`
	 * fully answers both.
	 */
	const [open, setOpen] = useState(true)

	return (
		<SidebarGroup>
			<SidebarGroupLabel>Dashboards</SidebarGroupLabel>
			<SidebarMenu>
				{/*
				 * `render={<SidebarMenuItem />}` makes the Collapsible root BE the
				 * `<li>` rather than wrap it, so the parent link and the trigger
				 * below stay direct siblings under it — `SidebarMenuAction`'s
				 * `peer-hover/menu-button:…` styling and its absolute positioning
				 * both key off that sibling relationship
				 * (`components/ui/sidebar.tsx`), and an extra wrapper div here would
				 * silently break both.
				 */}
				<Collapsible
					open={open}
					onOpenChange={setOpen}
					render={<SidebarMenuItem />}
				>
					{collapsedToIcon ? (
						/*
						 * Same P-19 triple, same reasoning, as the entries below: both
						 * fallbacks are the plain parent link rather than a spinner or an
						 * error panel, so the nav rail is whole in every state and the
						 * boundary still reports to Sentry.
						 */
						<ErrorBoundary
							feature="app-shell"
							section="sidebar-dashboards-collapsed-menu"
							fallback={() => <DashboardsParentLink />}
						>
							<Suspense fallback={<DashboardsParentLink />}>
								<SidebarDashboardsCollapsedMenu />
							</Suspense>
						</ErrorBoundary>
					) : (
						<DashboardsParentLink />
					)}

					{/*
					 * The chevron is its OWN button with its OWN accessible name — never
					 * the label's click handler wearing a second hat. Owner ruling: the
					 * "Custom Dashboards" label keeps navigating, full stop, so merging
					 * the two targets was explicitly rejected rather than overlooked.
					 * The name spells the ACTION the click performs next ("Collapse…" /
					 * "Expand…"), which is also how it reflects state — a screen-reader
					 * user hears what changed, not just an `aria-expanded` bit
					 * (`CollapsibleTrigger` sets that too, for free, as a second signal).
					 *
					 * `SidebarMenuAction` already carries
					 * `group-data-[collapsible=icon]:hidden` (`components/ui/sidebar.tsx`),
					 * so it vanishes together with the `SidebarMenuSub` it controls the
					 * moment the RAIL goes icon-collapsed — there is nothing for this
					 * toggle to reconcile with the hover popover above; that branch owns
					 * reachability in that state entirely on its own.
					 */}
					<CollapsibleTrigger
						render={
							<SidebarMenuAction
								aria-label={
									open
										? 'Collapse Custom Dashboards list'
										: 'Expand Custom Dashboards list'
								}
							/>
						}
					>
						<ChevronDown
							className={cn(
								'transition-transform duration-150',
								!open && '-rotate-90',
							)}
						/>
					</CollapsibleTrigger>

					<CollapsibleContent>
						<SidebarMenuSub>
							{/*
							 * The entries are a self-fetching lazy section, so they carry the
							 * P-19 triple. Both fallbacks are null ON PURPOSE, not a swallowed
							 * failure (P-17): a nav rail gets no spinner and no error panel —
							 * the static parent + Library links around it stay the whole,
							 * working nav, and the boundary still reports to Sentry. The page
							 * behind the parent link owns SHOWING the config failure.
							 */}
							<ErrorBoundary
								feature="app-shell"
								section="sidebar-dashboard-entries"
								fallback={() => null}
							>
								<Suspense fallback={null}>
									<SidebarDashboardEntries />
								</Suspense>
							</ErrorBoundary>

							{/*
							 * Same P-19/P-17 shape as the entries above, fallback `LibraryLink`
							 * rather than `null`: unlike the shortcuts, the Library row is
							 * static nav that must always render — only the "(N)" it can grow
							 * is optional.
							 */}
							<ErrorBoundary
								feature="app-shell"
								section="sidebar-library-count"
								fallback={() => <LibraryLink />}
							>
								<Suspense fallback={<LibraryLink />}>
									<SidebarLibraryLink />
								</Suspense>
							</ErrorBoundary>
						</SidebarMenuSub>
					</CollapsibleContent>
				</Collapsible>
			</SidebarMenu>
		</SidebarGroup>
	)
}
